import { getRequiresCentrifuge, findEligibleNurseIDs, objectFilter } from '@caresend/utils';

/**
 * Filter nurses by offer deadline. If the offer deadline is in the past,
 * the nurse is not eligible to fill the visit.
 */
const filterNursesByOfferDeadline = (nurses, timeSlotStartTime) => {
    const filteredNurses = objectFilter(nurses, (nurse) => {
        const { offerDeadline } = nurse;
        if (offerDeadline === 0)
            return true;
        return timeSlotStartTime - Date.now() >= offerDeadline;
    });
    return filteredNurses;
};
const filterNursesByOfficeAffiliation = (nurses, officeID, createdByOfficeID) => objectFilter(nurses, (nurse) => {
    var _a, _b;
    const nurseOfficeAffiliations = Object.keys((_a = nurse.officeAffiliations) !== null && _a !== void 0 ? _a : {});
    const isAffiliated = (_b = (nurse.allOfficeAffiliations
        || (officeID && nurseOfficeAffiliations.includes(officeID))
        || (createdByOfficeID && nurseOfficeAffiliations.includes(createdByOfficeID)))) !== null && _b !== void 0 ? _b : false;
    return isAffiliated === true;
});
/**
 * Calculate the expected fill probability for a given set of nurses. This
 * represents the probability that at least one nurse will be available to fill
 * a visit, calculated as 1 minus the probability that no nurses can fill it.
 * This value is compared to the office's fill threshold to determine if a
 * timeslot is to be shown.
 */
const calculateExpectedFillProbability = (nurses) => 1 - Object.values(nurses).reduce((acc, nurse) => acc * (1 - nurse.fillProbability), 1);
/**
 * Get the probability that a visit won't be fulfilled by a nurse
 * This is used by the nurse filter to determine if a visit can be fulfilled
 * based on the office's fill probability threshold
 */
const getNurseFillProbabilityFailure = (params, availableNurses, visitFillThreshold) => {
    const { createdByOfficeID, officeID } = params.draftWaypoint;
    if (!createdByOfficeID && !officeID)
        return 'Missing office ID';
    try {
        const nursesFilteredByOfficeAffiliation = filterNursesByOfficeAffiliation(availableNurses, officeID, createdByOfficeID);
        const nursesFilteredByOfferDeadline = filterNursesByOfferDeadline(nursesFilteredByOfficeAffiliation, params.timeSlot.startTime);
        if (!Object.keys(nursesFilteredByOfferDeadline).length) {
            throw Error('No nurses found for office');
        }
        const expectedFillProbability = calculateExpectedFillProbability(nursesFilteredByOfferDeadline);
        if (expectedFillProbability < visitFillThreshold) {
            return 'Expected fill probability is too low';
        }
    }
    catch (error) {
        return error;
    }
    return null;
};
/**
 * Get the filtered nurses or a failure message for the nurse availability
 * based on nurse active status and centrifuge requirement. Pending nurses are
 * disregarded if the pending nurse ID is not enabled in the scheduling config.
 * This is used by the nurse filter to determine if a visit can be fulfilled
 * based on the nurse availability
 */
const getNurseAvailabilitiesOrFailure = (params, usePendingNurse) => {
    var _a;
    const requiresCentrifuge = (_a = getRequiresCentrifuge(params)) !== null && _a !== void 0 ? _a : false;
    const eligibleNurses = findEligibleNurseIDs(params, requiresCentrifuge, usePendingNurse);
    if (!Object.keys(eligibleNurses).length) {
        return `No nurse ${requiresCentrifuge ? 'with centrifuge' : ''}available`;
    }
    return eligibleNurses;
};

export { getNurseAvailabilitiesOrFailure, getNurseFillProbabilityFailure };
